import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { saveDeviceToken, removeDeviceToken } from '../lib/supabase';

const isNative = () => Capacitor.isNativePlatform();

// Persisted locally so we can remove the token on "toggle off" even across app restarts
// (the native 'registration' event only fires once per register() call, not on every launch).
const TOKEN_STORAGE_KEY = 'ilik_device_token';

let listenersRegistered = false;

const registerListeners = () => {
  if (listenersRegistered) return;
  listenersRegistered = true;

  PushNotifications.addListener('registration', async (token) => {
    console.log('[PushNotifications] registration: token received');
    const platform = Capacitor.getPlatform(); // 'ios' | 'android'
    localStorage.setItem(TOKEN_STORAGE_KEY, token.value);
    const saved = await saveDeviceToken(token.value, platform);
    console.log('[PushNotifications] saveDeviceToken result:', saved);
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('[PushNotifications] registrationError:', JSON.stringify(err));
  });

  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[PushNotifications] pushNotificationReceived:', JSON.stringify(notification));
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[PushNotifications] pushNotificationActionPerformed:', JSON.stringify(action));
  });
};

// Requests permission and registers for push. Returns true if permission was
// granted (and registration was attempted), false if denied or on native error.
export const enablePushNotifications = async () => {
  if (!isNative()) return true;
  try {
    registerListeners();
    const permStatus = await PushNotifications.requestPermissions();
    console.log('[PushNotifications] requestPermissions result:', permStatus?.receive);
    if (permStatus?.receive !== 'granted') {
      return false;
    }
    await PushNotifications.register();
    console.log('[PushNotifications] register() called');
    return true;
  } catch (err) {
    console.error('[PushNotifications] enablePushNotifications error:', err?.message || err);
    return false;
  }
};

// Removes this device's token from Supabase. Safe to call even if no token
// was ever captured this session (falls back to the locally persisted one).
export const disablePushNotifications = async () => {
  if (!isNative()) return true;
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      const removed = await removeDeviceToken(token);
      console.log('[PushNotifications] removeDeviceToken result:', removed);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
    return true;
  } catch (err) {
    console.error('[PushNotifications] disablePushNotifications error:', err?.message || err);
    return false;
  }
};
